/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable array-callback-return */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { withStyles } from '@material-ui/core';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  ThemeProvider,
  createTheme
} from '@mui/material';

import { setPageTitle, showToast } from '../../layout/layout.actions';
import { TOAST_MESSAGE_SEVERITY_ERROR, TOAST_MESSAGE_SEVERITY_SUCCESS } from '../../layout/types';

import { getMultiClientUsers, createMultiClientUser, updateMultiClientUserAccess, deleteMultiClientUser } from '../clients.actions';

import {
  POST_MULTI_CLIENT_USER_SUCCESS,
  POST_MULTI_CLIENT_USER_FAILURE,
  UPDATE_MULTI_CLIENT_USER_ACCESS_SUCCESS,
  UPDATE_MULTI_CLIENT_USER_ACCESS_FAILURE,
  GET_MULTI_CLIENT_USERS_SUCCESS,
  GET_MULTI_CLIENT_USERS_FAILURE,
  DELETE_MULTI_CLIENT_USER_SUCCESS,
  DELETE_MULTI_CLIENT_USER_FAILURE
} from '../types';

import InternalMultiClientUsersGrid from './internalMultiClientUsersGrid.component';

import { COLOR_PRIMARY, COLOR_SECONDARY, COLOR_WARNING } from 'styles/theme';

const styles = theme => ({
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2)
  },
  title: {
    fontWeight: 500
  },
  emailField: {
    minWidth: 400
  }
});

const InternalMultiClientUsersView = ({
  classes,
  setPageTitle,
  showToast,
  getMultiClientUsers,
  createMultiClientUser,
  updateMultiClientUserAccess,
  deleteMultiClientUser
}) => {
  const theme = createTheme({
    palette: {
      primary: COLOR_PRIMARY,
      secondary: COLOR_SECONDARY,
      warning: COLOR_WARNING
    }
  });

  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    setPageTitle('Internal Multi-Client Users');
    (async () => {
      const resp = await getMultiClientUsers();
      if (resp.type === GET_MULTI_CLIENT_USERS_SUCCESS) {
        setUsers(resp.response || []);
      } else if (resp.type === GET_MULTI_CLIENT_USERS_FAILURE) {
        showToast(resp.error || resp.payload || 'Failed to fetch multi-client users.', {
          severity: TOAST_MESSAGE_SEVERITY_ERROR
        });
      }
      setIsLoading(false);
    })();
  }, [setPageTitle, getMultiClientUsers]);

  const closeImportDialog = () => {
    if (isSaving) {
      return;
    }

    setEmail('');
    setIsImportDialogOpen(false);
  };

  const importUser = async () => {
    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail) {
      showToast('Please enter an email address.', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR
      });
      return;
    }

    if (!/^[^\s@]+@trcgm\.com$/i.test(normalizedEmail)) {
      showToast('Only @trcgm.com email addresses can be imported.', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR
      });
      return;
    }

    const verified = window.confirm(`Verify that ${normalizedEmail} should receive access to all clients?`);

    if (!verified) {
      return;
    }

    setIsSaving(true);

    const result = await createMultiClientUser(normalizedEmail);

    setIsSaving(false);

    if (result.type === POST_MULTI_CLIENT_USER_FAILURE) {
      showToast(result.messages?.[0] || 'The user could not be imported.', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR
      });
      return;
    }

    if (result.type === POST_MULTI_CLIENT_USER_SUCCESS) {
      showToast('Multi-client user imported successfully.', {
        severity: TOAST_MESSAGE_SEVERITY_SUCCESS
      });

      closeImportDialog();
      const resp = await getMultiClientUsers();
      setUsers(resp.response || []);
    }
  };

  const changeAccess = async user => {
    const emailOrId = user.auth0EmailAddress || user.auth0UserId;
    const shouldRevoke = !user.hasAccessRevoked;

    const actionText = shouldRevoke ? 'revoke all-client access from' : 'restore all-client access for';

    const verified = window.confirm(`Verify that you want to ${actionText} ${emailOrId}?`);

    if (!verified) {
      return;
    }

    setIsSaving(true);

    const result = await updateMultiClientUserAccess(user.internalMultiClientUserId, shouldRevoke);

    setIsSaving(false);

    if (result.type === UPDATE_MULTI_CLIENT_USER_ACCESS_FAILURE) {
      showToast(result.error || result.payload || 'The user access could not be updated.', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR
      });
      return;
    }

    if (result.type === UPDATE_MULTI_CLIENT_USER_ACCESS_SUCCESS) {
      showToast(shouldRevoke ? 'All-client access revoked.' : 'All-client access restored.', {
        severity: TOAST_MESSAGE_SEVERITY_SUCCESS
      });

      const resp = await getMultiClientUsers();
      setUsers(resp.response || []);
    }
  };

  const deleteUser = async user => {
    const emailOrId = user.auth0EmailAddress || user.auth0UserId;

    const verified = window.confirm(`Verify that you want to delete ${emailOrId}?`);

    if (!verified) {
      return;
    }

    setIsSaving(true);

    const result = await deleteMultiClientUser(user.internalMultiClientUserId);

    setIsSaving(false);

    if (result.type === DELETE_MULTI_CLIENT_USER_FAILURE) {
      showToast(result.error || result.payload || 'The user could not be deleted.', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR
      });
      return;
    }

    if (result.type === DELETE_MULTI_CLIENT_USER_SUCCESS) {
      showToast('User deleted successfully.', {
        severity: TOAST_MESSAGE_SEVERITY_SUCCESS
      });

      const resp = await getMultiClientUsers();
      setUsers(resp.response || []);
    }
  };

  return (
    <Box p={2}>
      <Box className={classes.header}>
        <Typography variant="h5" className={classes.title}>
          Multi-Client Users
        </Typography>

        <Button
          color="secondary"
          variant="contained"
          sx={{
            maxHeight: '40px',
            marginRight: '10px',
            marginTop: '8px',
            backgroundColor: '#5D9878',
            '&:hover': { backgroundColor: '#528569' }
          }}
          onClick={() => setIsImportDialogOpen(true)}
          disabled={isSaving}
        >
          Import User
        </Button>
      </Box>

      <InternalMultiClientUsersGrid
        users={users}
        isLoading={isLoading || isSaving}
        onAccessChange={changeAccess}
        onDeleteUser={deleteUser}
      />
      <ThemeProvider theme={theme}>
        <Dialog open={isImportDialogOpen} onClose={closeImportDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Import Multi-Client User</DialogTitle>

          <DialogContent>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              The user must already exist in Auth0 and use a @trcgm.com email address.
            </Typography>

            <TextField
              className={classes.emailField}
              autoFocus
              fullWidth
              margin="normal"
              label="Email address"
              type="email"
              value={email}
              onChange={event => setEmail(event.target.value)}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  importUser();
                }
              }}
              disabled={isSaving}
            />
          </DialogContent>

          <DialogActions>
            <Button onClick={closeImportDialog} disabled={isSaving}>
              Cancel
            </Button>

            <Button
              color="secondary"
              variant="contained"
              sx={{
                maxHeight: '40px',
                marginRight: '10px',
                marginTop: '8px',
                backgroundColor: '#5D9878',
                '&:hover': { backgroundColor: '#528569' }
              }}
              onClick={importUser}
              disabled={isSaving}
            >
              Verify & Import
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    </Box>
  );
};

InternalMultiClientUsersView.propTypes = {
  classes: PropTypes.object.isRequired,
  users: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  setPageTitle: PropTypes.func.isRequired,
  showToast: PropTypes.func.isRequired,
  getMultiClientUsers: PropTypes.func.isRequired,
  createMultiClientUser: PropTypes.func.isRequired,
  updateMultiClientUserAccess: PropTypes.func.isRequired,
  deleteMultiClientUser: PropTypes.func.isRequired
};

export default compose(
  withStyles(styles),
  connect(null, {
    setPageTitle,
    showToast,
    getMultiClientUsers,
    createMultiClientUser,
    updateMultiClientUserAccess,
    deleteMultiClientUser
  })
)(InternalMultiClientUsersView);
